import * as React from 'react';
import { AppDispatch } from '@app/store';
import UserMgmtApi from '@app/api/user-api/user.actions';
import { textInputValidate, isValidEmail } from '@app/lib/validator';
import { formatDate } from '@app/lib/functions';
import { capitalizeFirstLetter } from '@app/utils/utils';
import { Saver } from '@app/components/Saver';
import { ExclamationCircleIcon, EllipsisVIcon } from '@patternfly/react-icons';
import { PageSection, Title, TextInput, FormGroup, Select, SelectOption, SelectVariant, Modal, ModalVariant, Alert, AlertGroup, AlertVariant, Button as PFButton } from '@patternfly/react-core';
import { PatternflyTable } from '@app/components/PatternflyTable';
import { sortable, cellWidth } from '@patternfly/react-table';
import './Admin.css';
import { State } from '@app/store/root-reducer';
import { connect } from 'react-redux';
import { Card, CardBody, Button, CardHeader, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input } from 'reactstrap';
import { from } from '@apollo/client';
import { useState, useCallback, useEffect } from 'react';
import { Column, ColumnFiltersState, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, Updater, useReactTable } from '@tanstack/react-table';
import { MdArrowDownward, MdArrowUpward, MdCircle } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import UserSession from '@app/common/user-session';
import { useSnackbar } from 'notistack';

// Type for validation state
type ValidatedState = 'success' | 'warning' | 'error' | 'default';

interface UsersProps {
  partnerId: string;
  partnerName: string;
}

const UserActionsMenu = ({ user, onChangeRole, onChangeStatus, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isEnabled = user.account_status?.toLowerCase() === 'active';

  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen(open => !open)} direction="start">
      <DropdownToggle
        color="link"
        className="user-actions-toggle"
        aria-label={`Actions for ${user.first_name} ${user.last_name}`}
      >
        <EllipsisVIcon />
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem onClick={() => onChangeRole(user)}>Change Access Level</DropdownItem>
        <DropdownItem divider />
        <DropdownItem
          className={isEnabled ? 'user-action-danger' : ''}
          onClick={() => onChangeStatus(user)}
        >
          {isEnabled ? 'Disable User' : 'Enable User'}
        </DropdownItem>
        <DropdownItem divider />
        <DropdownItem className="user-action-danger" onClick={() => onDelete(user)}>
          Delete User
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

const Users = ({ partnerId, partnerName }: UsersProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [updateRoles, setUpdateRoles] = useState([]);
  const [role, setRole] = useState('Viewer');
  const [updatedRole, setUpdatedRole] = useState('');
  const [updatedStatus, setUpdatedStatus] = useState('');
  const [actionId, setActionId] = useState('');
  const [isRoleOpen, setIsRoleOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isUpdateRoleModalOpen, setIsUpdateRoleModalOpen] = useState(false);
  const [isUpdateRoleOpen, setIsUpdateRoleOpen] = useState(false);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<'success' | 'danger' | 'warning'>('success');
  const { enqueueSnackbar } = useSnackbar();

  const loadUsers = useCallback(async () => {
    try {
      const userList = await UserMgmtApi.getAll();
      const rolesList = getRoleOptions('invite');
      const updateRolesList = getRoleOptions('update');

      setRoles(rolesList);
      setUpdateRoles(updateRolesList);
      setUsers(userList);
    } catch (error) {
      setMessage('There was an error getting the users');
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );

  const columns = React.useMemo(() => [
    {
      accessorKey: 'first_name',
      header: 'Name',
      cell: ({ row }) => (
        <>
          {row.original.first_name + ' ' + row.original.last_name}
        </>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'role',
      header: 'Role',
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <>
          {capitalizeFirstLetter(row.original.role)}
        </>
      ),
    },
    {
      accessorKey: 'account_status',
      header: 'Status',
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="text-left">
          {row.original.account_status}
        </div>
      ),
    },
    {
      accessorKey: 'last_login',
      header: 'Last Active',
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        // Handle pending status explicitly
        if (row.original.account_status?.toLowerCase() === 'pending' || 
            row.original.status?.toLowerCase() === 'pending') {
          return 'None';
        }
        
        return formatDate(row.original.last_login, false);
      },
    },
    {
      id: 'actions',
      header: '',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <UserActionsMenu
          user={row.original}
          onChangeRole={showUpdateRoleModal}
          onChangeStatus={showUpdateStatusModal}
          onDelete={showDeleteModal}
        />
      ),
    },
  ], []);

  const table = useReactTable({
    data: users,
    columns,
    filterFns: {},
    state: {
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 10,
        pageIndex: 0,
      },
    },
  });

  const formatDate = (dateString, showTime = false) => {
    if (!dateString) return '';
    
    // Check for default/placeholder dates like 0001-01-01T00:00:00Z
    if (dateString.includes('0001-01-01') || dateString.includes('0000-00-00')) {
      return 'None';
    }
    
    try {
      const options = { 
        year: 'numeric' as const, 
        month: 'long' as const, 
        day: 'numeric' as const 
      };
      const date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'None';
      }
      
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'None';
    }
  };

  const userFromItem = (item) => {
    const status = item.account_status;
    return {
      cells: [
        { title: item.first_name + ' ' + item.last_name },
        { title: status },
        { title: capitalizeFirstLetter(item.role) },
        { title: status?.toLowerCase() === 'pending' ? 'None' : formatDate(item.last_login, false) },
        { title: item.email },
        { title: item.id, props: { className: 'pf-m-hidden pf-m-hidden-on-md' } }
      ],
    };
  };

  const getColumns = () => [
    { title: 'Name', transforms: [sortable] },
    { title: 'Status' },
    { title: 'Role' },
    { title: 'Last Login' },
    { title: 'Email', transforms: [sortable] },
    { title: 'ID', props: { className: 'pf-m-hidden pf-m-hidden-on-md' } },
    { title: 'Actions', transforms: [cellWidth(10)] },
  ];

  const onRoleToggle = (isOpen) => setIsRoleOpen(isOpen);

  const onRoleSelect = (event, selection, isPlaceholder) => {
    if (!isPlaceholder) {
      setRole(selection);
      setIsRoleOpen(false);
    }
  };

  const onUpdateRoleToggle = (isOpen) => setIsUpdateRoleOpen(isOpen);

  const onUpdateRoleSelect = (event, selection, isPlaceholder) => {
    if (!isPlaceholder) {
      setUpdatedRole(capitalizeFirstLetter(selection));
      setIsUpdateRoleOpen(false);
    }
  };

  // Updated function to handle standard select Input events
  const handleRoleChange = (e) => {
    setUpdatedRole(e.target.value);
  };

  const showUpdateRoleModal = (user) => {
    setIsUpdateRoleModalOpen(true);
    setUpdatedRole(capitalizeFirstLetter(user.role));
    setUserEmail(user.email);
    setUserName(`${user.first_name} ${user.last_name}`.trim());
    setActionId(user.id);
  };

  const showUpdateStatusModal = (user) => {
    setIsUpdateStatusModalOpen(true);
    setUserEmail(user.email);
    setUserName(`${user.first_name} ${user.last_name}`.trim());
    setUpdatedStatus(user.account_status);
    setActionId(user.id);
  };

  const showDeleteModal = (user) => {
    setUserEmail(user.email);
    setUserName(`${user.first_name} ${user.last_name}`.trim());
    setActionId(user.id);
    setIsDeleteModalOpen(true);
  };

  const getColumnIndex = (columnName) => {
    const columns = getColumns();
    return columns.findIndex(col => col.title === columnName);
  };

  const updateRole = async () => {
    const payload = { change: { role: updatedRole.toLowerCase() } };

    try {
      // Call the API to update user role
      await UserMgmtApi.update(actionId, payload);
      
      // Update the user data in the table
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === actionId) {
            return {
              ...user,
              role: updatedRole.toLowerCase()
            };
          }
          return user;
        })
      );

      // Close modal and show success message
      setIsUpdateRoleModalOpen(false);
      showNotification(`Successfully updated the user role to ${updatedRole}.`, 'success');
    } catch (error: any) {
      console.log("Role update error:", error);
      
      // Close the modal regardless of error
      setIsUpdateRoleModalOpen(false);
      
      // Determine if this is an actual error that should show an error message
      const isRealError = error?.response && error?.response?.status >= 400;
      
      if (isRealError) {
        // Show appropriate error message
        const errorMessage = getRoleUpdateErrorMessage(error, actionId, updatedRole);
        showNotification(errorMessage, 'danger');
      } else {
        setUsers(prevUsers => 
          prevUsers.map(user => {
            if (user.id === actionId) {
              return {
                ...user,
                role: updatedRole.toLowerCase()
              };
            }
            return user;
          })
        );
        
        // Show success message
        showNotification(`Successfully updated the user role to ${updatedRole}.`, 'success');
      }
    }
  };

  const updateStatus = async () => {
    try {
      // Call the API to update user status
      await UserMgmtApi.updateAccountStatus(actionId);
      
      // Calculate the new status (toggle from current status)
      const newStatus = updatedStatus?.toLowerCase() === 'active' ? 'Disabled' : 'Active';
      
      // Update the user data in the table
      setUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.id === actionId) {
            return {
              ...user,
              account_status: newStatus
            };
          }
          return user;
        })
      );

      // Close modal and show success message
      setIsUpdateStatusModalOpen(false);
      showNotification(`Successfully updated the user status to ${newStatus}.`, 'success');
    } catch (error: any) {
      console.log("Status update error:", error);
      
      // Close the modal regardless of error
      setIsUpdateStatusModalOpen(false);
      
      // Determine if this is an actual error that should show an error message
      const isRealError = error?.response && error?.response?.status >= 400;
      
      if (isRealError) {
        // Show appropriate error message
        const errorMessage = getStatusUpdateErrorMessage(error, actionId);
        showNotification(errorMessage, 'danger');
      } else {
        const newStatus = updatedStatus?.toLowerCase() === 'active' ? 'Disabled' : 'Active';
        
        setUsers(prevUsers => 
          prevUsers.map(user => {
            if (user.id === actionId) {
              return {
                ...user,
                account_status: newStatus
              };
            }
            return user;
          })
        );
        
        // Show success message
        showNotification(`Successfully updated the user status to ${newStatus}.`, 'success');
      }
    }
  };

  const deleteUser = async () => {
    setIsLoading(true);

    try {
      await UserMgmtApi.deleteUser(actionId);
      setUsers(prevUsers => prevUsers.filter(user => user.id !== actionId));
      setIsDeleteModalOpen(false);
      showNotification(`Successfully deleted ${userEmail}.`, 'success');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'There was an error deleting the user.';
      showNotification(errorMessage, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to analyze error responses and return appropriate messages
  const getStatusUpdateErrorMessage = (error, userId) => {
    // Check if it's our own user ID (self-modification attempt)
    const currentUserId = UserSession.getParam('userId');
    const userRole = UserSession.getParam('role').toLowerCase();
    
    if (userId === currentUserId) {
      return 'You cannot change your own account status. Ask another manager to perform this action.';
    }
    
    // Check error response for specific error codes/messages from backend
    if (error?.response?.data?.error) {
      const errorCode = error.response.data.error;
      
      switch(errorCode) {
        case 'LAST_MANAGER':
          return 'Cannot disable the last active manager account. Please ensure there is at least one other active manager.';
        case 'INSUFFICIENT_PERMISSIONS':
          return `You need manager permissions to change user status. Your current role (${userRole}) doesn't have sufficient privileges.`;
        case 'USER_NOT_FOUND':
          return 'The user account no longer exists or has been removed.';
        default:
          // If there's a message but not one we explicitly handle
          return error.response.data.message || 'An unexpected error occurred while updating the user status.';
      }
    }
    
    // Check for network or authentication issues
    if (error?.message?.includes('Network Error')) {
      return 'Network connection issue. Please check your internet connection and try again.';
    }
    
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      return 'You do not have permission to change this user\'s status. This action requires manager role.';
    }
    
    // Default fallback message
    return 'There was an error updating the user account status. Please try again or contact system administrator.';
  };

  // Helper function to analyze role update error responses
  const getRoleUpdateErrorMessage = (error, userId, newRole) => {
    // Check if it's our own user ID (self-modification attempt)
    const currentUserId = UserSession.getParam('userId');
    const userRole = UserSession.getParam('role').toLowerCase();
    
    if (userId === currentUserId) {
      return 'You cannot change your own role. Please ask another manager to make this change for you.';
    }
    
    // Check error response for specific error codes/messages from backend
    if (error?.response?.data?.error) {
      const errorCode = error.response.data.error;
      
      switch(errorCode) {
        case 'LAST_MANAGER':
          return 'You cannot downgrade the last remaining manager account. Please make sure there is at least one other active manager before proceeding.';
        case 'INSUFFICIENT_PERMISSIONS':
          return 'You do not have sufficient permissions to perform this action.';
        case 'USER_NOT_FOUND':
          return 'The user account no longer exists or has been removed.';
        case 'INVALID_ROLE':
          return `The role "${newRole}" is not a valid role in the system.`;
        default:
          // If there's a message but not one we explicitly handle
          return error.response.data.message || 'Error updating user role';
      }
    }
    
    // Check for network or authentication issues
    if (error?.message?.includes('Network Error')) {
      return 'Network connection issue. Please check your internet connection and try again.';
    }
    
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      return 'You do not have sufficient permissions to perform this action.';
    }
    
    // Default fallback message
    return 'Error updating user role';
  };

  const showNotification = (msg: string, type: 'success' | 'danger' | 'warning' = 'success') => {
    enqueueSnackbar(msg, { variant: type === 'danger' ? 'error' : type });
  };

  const getInviteUserErrorMessage = (error) => {
    if (error?.response?.data?.error) {
      switch (error.response.data.error) {
        case 'EMAIL_EXISTS':
          return 'A user with this email already exists.';
        case 'INVALID_EMAIL':
          return 'The provided email address is invalid.';
        default:
          return error.response.data.message || 'An error occurred while inviting the user.';
      }
    }
    if (error?.message?.includes('Network Error')) {
      return 'Network connection issue. Please check your internet connection and try again.';
    }
    if (error?.response?.status === 400) {
      return 'Invalid input. Please check the form fields and try again.';
    }
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      return 'You do not have permission to invite users.';
    }
    return 'User email already exists.';
  };

  const handleInviteUser = async (event) => {
    if (event) {
      event.preventDefault();
    }

    setIsLoading(true);
    
    if (!firstName.trim()) {
      showNotification('First name is required', 'danger');
      setIsLoading(false);
      return;
    }
    
    if (!lastName.trim()) {
      showNotification('Last name is required', 'danger');
      setIsLoading(false);
      return;
    }
    
    if (!email.trim() || !emailIsValid) {
      showNotification('Valid email is required', 'danger');
      setIsLoading(false);
      return;
    }
    
    const payload = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      role: role.toLowerCase(),
    };


    try {
      const result = await UserMgmtApi.inviteUser(payload);

      if (result?.data?.request_id) {
        const user = result.data.user;
        const fullName = user.first_name + ' ' + user.last_name;

        showNotification(`${fullName} was invited successfully!`, 'success');
        
        setFirstName('');
        setLastName('');
        setEmail('');
        setRole('Viewer');
        setIsInviteModalOpen(false);
        
        setUsers(prevUsers => [
          ...prevUsers,
          {
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            account_status: user.account_status,
            role: user.role,
            last_login: 'N/A',
            phone: user.phone,
            email: user.email,
          }
        ]);
      } else {
        showNotification(result?.data?.error || 'Unknown error occurred during invitation', 'danger');
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      showNotification(getInviteUserErrorMessage(error), 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleOptions = (uniqueVal) => {
    const allRoles = ['Editor', 'Manager', 'Viewer'];
    return allRoles.map((name, index) => (
      <SelectOption key={`role-${uniqueVal}-${index}`} value={name} />
    ));
  };

  const getStatusAction = () => {
    return updatedStatus?.toLowerCase() === 'active' ? 'Disable' : 'Enable';
  };

  const closeInviteModal = () => {
    if (isLoading) return;
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole('Viewer');
    setIsRoleOpen(false);
    setIsInviteModalOpen(false);
  };

  const emailIsValid = email ? isValidEmail(email) : false;
  const firstNameIsValid = firstName.trim().length > 0;
  const lastNameIsValid = lastName.trim().length > 0;
  
  const emailErrorState = email ? textInputValidate(email, true, emailIsValid, 'Invalid email format') : { validated: 'default', errorText: '' };
  const firstNameErrorState = firstName ? textInputValidate(firstName, true, firstNameIsValid, 'First name is required') : { validated: 'default', errorText: '' };
  const lastNameErrorState = lastName ? textInputValidate(lastName, true, lastNameIsValid, 'Last name is required') : { validated: 'default', errorText: '' };
  
  // Convert the validation state to PatternFly's expected types
  const getPatternFlyValidation = (state: string): ValidatedState => {
    switch(state) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'default';
    }
  };
  
  const emailValidated = getPatternFlyValidation(emailErrorState.validated);
  const firstNameValidated = getPatternFlyValidation(firstNameErrorState.validated);
  const lastNameValidated = getPatternFlyValidation(lastNameErrorState.validated);
  
  const statusAction = getStatusAction();
  const formValid = emailIsValid && firstNameIsValid && lastNameIsValid;

  return (
    <>
      <Col xs={12}>
        <Card className="users-card">
          <CardHeader className="users-card-header">
            <div className="w-100 d-flex align-items-center justify-content-between">
              <div className="icon-heading">Users</div>
              <Button className="ethica-button-green" onClick={() => setIsInviteModalOpen(true)}>
                Add user
              </Button>
            </div>
          </CardHeader>
          <CardBody className="font-12 profile-users">
            <table className="controllers-list-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th key={header.id} colSpan={header.colSpan} className={`text-left users-column-${header.column.id}`}>
                        {header.isPlaceholder ? null : (
                          <div className={`d-flex align-items-center ${header.column.id === 'actions' ? 'justify-content-center' : 'justify-content-start'}`}>
                            <div
                              {...{
                                className: header.column.getCanSort()
                                  ? 'select-none table-column-header'
                                  : 'table-column-header',
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: React.createElement(MdArrowUpward as any, { className: 'ms-2' }),
                                desc: React.createElement(MdArrowDownward as any, { className: 'ms-2' }),
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                            {header.column.getCanFilter() ? (
                              <div className="d-flex justify-content-end">
                                <Filter column={header.column} />
                              </div>
                            ) : null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <React.Fragment key={row.id}>
                      <tr>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className={`${cell.column.id === 'actions' ? 'text-center' : 'text-start'} users-column-${cell.column.id}`}>
                          <span className={`d-flex flex-wrap-nowrap w-100 ${cell.column.id === 'actions' ? 'justify-content-center' : 'justify-content-start'}`}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className="d-flex items-center justify-content-center align-items-center flex-wrap mt-2">
              <div className="me-2">
                <span className="flex items-center gap-1">
                  Go to page:
                  <input
                    type="number"
                    min="1"
                    max={table.getPageCount()}
                    defaultValue={table.getState().pagination.pageIndex + 1}
                    onChange={e => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0
                      table.setPageIndex(page)
                    }}
                    className="border p-1 rounded w-16"
                  />
                </span>
              </div>
              <div>
                  <button
                    className="border rounded p-1 me-1"
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                  >
                    {'<<'}
                  </button>
                  <button
                    className="border rounded p-1 me-1"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                  >
                    {'<'}
                  </button>
                  <span className="flex items-center me-1">
                    <strong>
                      {table.getState().pagination.pageIndex + 1} of{' '}
                      {table.getPageCount()}
                    </strong>
                  </span>
                  <button
                    className="border rounded p-1 me-1"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                  >
                    {'>'}
                  </button>
                  <button
                    className="border rounded p-1 me-2"
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                  >
                    {'>>'}
                  </button>
                </div>
                <div>
                  <select
                    className="form-select"
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value))
                    }}
                  >
                    {[10, 20, 30, 40, 50].map(pageSize => (
                      <option key={pageSize} value={pageSize}>
                        Show {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
            </div>
          </CardBody>
        </Card>
      </Col>

      <Modal
        variant={ModalVariant.small}
        title="Add user"
        isOpen={isInviteModalOpen}
        onClose={closeInviteModal}
        className="modal-fade-in"
        actions={[
          <PFButton
            key="add"
            variant="primary"
            onClick={handleInviteUser}
            isDisabled={!formValid || isLoading}
            className="ethica-button-green"
          >
            {isLoading ? 'Adding...' : 'Add'}
          </PFButton>,
          <PFButton key="cancel" variant="secondary" onClick={closeInviteModal} isDisabled={isLoading}>
            Cancel
          </PFButton>
        ]}
      >
        <form onSubmit={handleInviteUser} className="add-user-form">
          <FormGroup
            label="First Name"
            fieldId="first-name"
            isRequired
            className="form-item"
            helperTextInvalid={firstNameErrorState.errorText}
            helperTextInvalidIcon={<ExclamationCircleIcon />}
            validated={firstNameValidated}
          >
            <TextInput
              value={firstName}
              onChange={setFirstName}
              type="text"
              id="first-name"
              isRequired
              isDisabled={isLoading}
              validated={firstNameValidated}
            />
          </FormGroup>
          <FormGroup
            label="Last Name"
            fieldId="last-name"
            isRequired
            className="form-item"
            helperTextInvalid={lastNameErrorState.errorText}
            helperTextInvalidIcon={<ExclamationCircleIcon />}
            validated={lastNameValidated}
          >
            <TextInput
              value={lastName}
              onChange={setLastName}
              type="text"
              id="last-name"
              isRequired
              isDisabled={isLoading}
              validated={lastNameValidated}
            />
          </FormGroup>
          <FormGroup
            label="Email"
            fieldId="email"
            isRequired
            className="form-item"
            helperTextInvalid={emailErrorState.errorText}
            helperTextInvalidIcon={<ExclamationCircleIcon />}
            validated={emailValidated}
          >
            <TextInput
              value={email}
              onChange={setEmail}
              type="email"
              id="email"
              isRequired
              isDisabled={isLoading}
              validated={emailValidated}
            />
          </FormGroup>
          <FormGroup label="Role" fieldId="role" isRequired className="form-item">
            <Select
              toggleId="role"
              onToggle={onRoleToggle}
              isOpen={isRoleOpen}
              onSelect={onRoleSelect}
              selections={role}
              isDisabled={isLoading}
              variant={SelectVariant.single}
              aria-label="Select Role"
            >
              {roles}
            </Select>
          </FormGroup>
        </form>
      </Modal>

      <Modal
        variant={ModalVariant.small}
        title="Update User Role"
        isOpen={isUpdateRoleModalOpen}
        onClose={() => setIsUpdateRoleModalOpen(false)}
        className="modal-fade-in"
        actions={[
          <Button
            key="save"
            variant="primary"
            onClick={updateRole}
            isDisabled={isLoading}
            className="ethica-button-green"
          >
            Save
          </Button>,
          <Button 
            key="cancel" 
            variant="primary" 
            onClick={() => setIsUpdateRoleModalOpen(false)}
            className="ethica-button-cancel"
          >
            Cancel
          </Button>
        ]}
      >
        <div>
          <Input
            type="select" 
            value={updatedRole || ''}
            onChange={handleRoleChange}
            className="form-control"
          >
            <option key="viewer" value="Viewer">Viewer</option>
            <option key="editor" value="Editor">Editor</option>
            <option key="manager" value="Manager">Manager</option>
          </Input>
        </div>
      </Modal>

      <Modal
        variant={ModalVariant.small}
        title={`${statusAction} user ${userName}?`}
        isOpen={isUpdateStatusModalOpen}
        onClose={() => setIsUpdateStatusModalOpen(false)}
        className="modal-fade-in"
        actions={[
          <Button
            key="save"
            variant="primary"
            onClick={updateStatus}
            isDisabled={isLoading}
            className="ethica-button-green"
          >
            Save
          </Button>,
          <Button 
            key="cancel" 
            variant="primary" 
            onClick={() => setIsUpdateStatusModalOpen(false)}
            className="ethica-button-cancel"
          >
            Cancel
          </Button>
        ]}
      >
        <div>User Email: {userEmail}</div>
      </Modal>

      <Modal
        variant={ModalVariant.small}
        title={`Delete user ${userName}?`}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="modal-fade-in"
        actions={[
          <Button
            key="delete"
            variant="danger"
            onClick={deleteUser}
            disabled={isLoading}
            className="ethica-button-delete"
          >
            Delete
          </Button>,
          <Button
            key="cancel"
            variant="primary"
            onClick={() => setIsDeleteModalOpen(false)}
            disabled={isLoading}
            className="ethica-button-cancel"
          >
            Cancel
          </Button>
        ]}
      >
        <div>User Email: {userEmail}</div>
      </Modal>
    </>
  );
};

export default Users;

function setColumnFilters(updaterOrValue: Updater<ColumnFiltersState>): void {
  throw new Error('Function not implemented.');
}

function Filter({ column }: { column: Column<any, unknown> }) {
  const columnFilterValue = column.getFilterValue()
  const filterVariant = column.columnDef.meta ?? {}

  return filterVariant === 'range' ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'select' ? (
    <select
      onChange={e => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      <option value="">All</option>
      <option value="complicated">complicated</option>
      <option value="relationship">relationship</option>
      <option value="single">single</option>
    </select>
  ) : (
    <DebouncedInput
      className="form-control"
      onChange={value => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? '') as string}
    />
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  )
}
